"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Briefcase,
  Sparkles,
  Building2,
  ShieldCheck,
  Flame,
  Lock,
  Eye,
  Activity,
  Target,
  Users,
  Award,
  ArrowRight,
  Mic,
  Compass,
  TrendingUp,
  AlertTriangle,
  Scale,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TimelineBlock } from "@/lib/blocks/timeline-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "384", suffix: "x-1,416x", label: "Crisis Response Speed", sub: "Versus traditional approaches", accent: "denim" },
  { metric: "85", suffix: "-95%", label: "Crisis Success Rate", sub: "Crisis Response Engine deployment", accent: "red" },
  { metric: "89", suffix: "%", label: "Prediction Accuracy", sub: "Crisis forecasting", accent: "cyan" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "Zero security incidents", accent: "purple" },
  { metric: "300", suffix: "+", label: "Elite Clients", sub: "Enterprise and beyond", accent: "emerald" },
  { metric: "347", suffix: "%", label: "Average ROI", sub: "Three-year deployment", accent: "amber-gold" },
];

const CHALLENGES = [
  { icon: Eye, title: "Brand Protection at Scale", body: "Multinational corporations operate across jurisdictions where brand perception is shaped by forces that official communications cannot reach. The gap between what a corporation says about itself and what the market believes about it is where competitive advantage erodes.", accent: "red" },
  { icon: Flame, title: "Crisis Response in Hours, Not Weeks", body: "Ninety-five percent of crisis damage occurs within the first six hours. Conventional crisis response firms require days to assemble teams and develop strategy. By then, the damage window has closed and the narrative has been defined by others.", accent: "amber-gold" },
  { icon: TrendingUp, title: "Competitive Positioning Intelligence", body: "Market positioning is not a branding exercise. It is an intelligence operation. Understanding what competitors are doing, predicting their next moves, identifying their vulnerabilities — requires the analytical infrastructure that only exists within organizations built for sovereign-scale operations.", accent: "cyan" },
  { icon: Lock, title: "Secure Corporate Communications", body: "Multinational corporations transmit trade secrets, M&A strategies, board communications, and regulatory filings across digital infrastructure that adversaries actively target. The cost of a single breach averages millions in direct expenses — and the indirect costs far exceed the direct figure.", accent: "purple" },
  { icon: Target, title: "Digital Transformation Without the Trap", body: "Enterprise digital transformation initiatives fail at rates exceeding seventy percent when managed through conventional consulting approaches. The failure is structural: consultants advise but do not operate, design frameworks but do not execute, and move to the next engagement before the previous one has produced results.", accent: "emerald" },
  { icon: AlertTriangle, title: "Regulatory Complexity Across Jurisdictions", body: "Multinational corporations operate in regulatory environments that shift faster than internal compliance teams can track. A single regulatory change in one jurisdiction can cascade across global operations, affecting product launches, marketing strategies, data handling practices, and competitive positioning simultaneously.", accent: "denim" },
];

const PLATFORMS = [
  {
    title: "Crisis Response Engine",
    subtitle: "Crisis Transformation Engine",
    body: "Detects threats in under two minutes (compared to four to seventy-two hours for traditional approaches), completes assessment in under thirty seconds, generates response strategy in under two minutes, achieves full deployment in under five minutes. The Golden Hour Principle addressed through architecture that compresses the critical damage window by ninety-nine point nine percent.",
    accent: "red",
  },
  {
    title: "Intelligence Operations Layer",
    subtitle: "AI-Driven Digital Intelligence",
    body: "Real-time social listening, big data mining, public sentiment analysis, threat detection, predictive analytics across two hundred-plus platforms, one hundred thousand-plus news sources, one thousand-plus dark web sources. Eighty-nine percent crisis prediction accuracy, eighty-five percent sentiment prediction, seventy-five percent competitive move anticipation.",
    accent: "cyan",
  },
  {
    title: "Narrative Engineering Layer",
    subtitle: "Autonomous Perception Proliferation",
    body: "Engineers brand visibility at scale. Deploys content across fifty-plus digital platforms achieving ninety-five to ninety-eight percent organic authenticity. Four core engines: Organic Simulation Engine (up to one million concurrent virtual personas), Narrative Generation Engine (one thousand to one hundred thousand content variants per campaign).",
    accent: "purple",
  },
  {
    title: "Security Operations Layer",
    subtitle: "Sovereign Security System",
    body: "Zero-trust security architecture providing encrypted communications, network hardening, threat detection, cyber forensics. Ninety-nine point nine nine nine nine percent uptime. Zero security incidents across fifteen-plus years. Mean time to detection improvement from two hundred twelve days to fourteen hours — a ninety-three point five percent improvement.",
    accent: "amber-gold",
  },
];

const PILLARS = [
  { icon: Eye, title: "Perception, Reputation & Public Relations", body: "For enterprise clients, the Perception pillar addresses the brand visibility challenge — what stakeholders, markets, regulators, and the public believe about the organization. Crisis response, brand protection, market positioning, and competitive narrative management.", accent: "red" },
  { icon: Lock, title: "Privacy, Encryption & Information Security", body: "Enterprise data — trade secrets, M&A strategies, board communications, regulatory filings, employee records — demands the same security infrastructure that protects national defense communications. Sovereign-grade encryption, zero-trust architecture, air-gapped deployment for sensitive operations.", accent: "purple" },
  { icon: Award, title: "Policy, Governance & Transformation", body: "Digital transformation, compliance automation, governance infrastructure, regulatory navigation. The same platform architecture that serves national governments processing data for nine hundred million citizens serves Fortune 500 enterprise operations — with the operational headroom to handle any data flow.", accent: "emerald" },
];

const SERVICES = [
  { title: "Brand Protection & Visibility Engineering", body: "Narrative Engineering Layer engineers brand visibility at scale with three hundred to five hundred percent improvement in narrative penetration. Continuous monitoring across two hundred-plus platforms. Counter-narrative deployment, search result optimization, and competitive narrative management across every digital surface.", accent: "red" },
  { title: "Crisis Response & Transformation", body: "Crisis Response Engine deployment for crisis containment, reputation recovery, negative content neutralization. Three hundred eighty-four to one thousand four hundred sixteen times the speed of traditional crisis management. Eighty-five to ninety-five percent success rates converting crises into narrative reinforcement opportunities.", accent: "amber-gold" },
  { title: "Competitive Intelligence & Market Positioning", body: "Intelligence Operations Layer provides competitive intelligence across two hundred-plus platforms. Seventy-five percent competitive move anticipation. Eighty percent regulatory prediction accuracy across one hundred ninety-five-plus countries. The intelligence architecture that converts market uncertainty into strategic advantage.", accent: "cyan" },
  { title: "Secure Corporate Communications", body: "Security Operations Layer zero-trust architecture for board communications, M&A strategy protection, trade secret security, and regulatory filing confidentiality. Quantum-resistant encryption, FIPS 140-3 Level 3 hardware security modules, and air-gapped deployment capability.", accent: "purple" },
  { title: "Digital Transformation & Operational Excellence", body: "The same platform architecture that serves national governments — managing complexity across eighteen countries and processing data for over nine hundred million citizens — applied to enterprise transformation. Seventy-two percent staff productivity improvement. Sixty-five percent inter-departmental coordination improvement.", accent: "emerald" },
  { title: "Regulatory Navigation & Compliance", body: "Real-time regulatory monitoring across one hundred ninety-five-plus countries. Predictive regulatory change forecasting with eighty percent accuracy. Compliance automation across GDPR, HIPAA, PCI DSS, and fifty-plus pre-built frameworks. Transform compliance from reactive cost center into proactive strategic advantage.", accent: "denim" },
];

const CLIENTS = [
  { title: "Fortune 100 Enterprises", body: "Multinational corporations operating across multiple jurisdictions, requiring brand protection at continental scale, crisis response measured in hours, and operational transformation delivered by an operator who runs systems, not produces reports.", accent: "denim" },
  { title: "Global Financial Institutions", body: "Banks, investment firms, and insurance companies requiring secure communications, regulatory compliance across jurisdictions, crisis response capability, and competitive intelligence in markets where information asymmetry determines outcomes.", accent: "red" },
  { title: "Technology & Telecommunications", body: "Technology companies and telecom operators facing rapid market evolution, regulatory complexity, and competition from both incumbents and disruptors. Brand protection, market positioning, and operational transformation at sovereign-grade.", accent: "cyan" },
  { title: "Energy & Industrial Conglomerates", body: "Energy companies, manufacturers, and industrial operators facing operational complexity, supply chain security, regulatory navigation, and stakeholder management across multiple jurisdictions and stakeholder categories.", accent: "amber-gold" },
  { title: "Pharmaceuticals & Life Sciences", body: "Pharmaceutical, biotechnology, and medical device companies requiring regulatory navigation, crisis response capability, stakeholder trust management, and operational transformation in highly regulated environments.", accent: "emerald" },
  { title: "Professional Services & Consulting", body: "Law firms, management consultancies, and advisory practices where reputation, confidentiality, and stakeholder trust are foundational to client relationships. Sovereign-grade protection for the most sensitive client engagements.", accent: "purple" },
];

export default function CorporateEnterprisePage() {
  return (
    <>
      <PageHero
        icon={Briefcase}
        overline="Corporate & Enterprise"
        overlineClassName="text-denim"
        title={
          <>
            Operational Excellence <span className="text-gradient-premium">at Fortune Scale.</span>
          </>
        }
        subtitle="Fortune 100 enterprises and multinational corporations operating across jurisdictions face a specific constellation of challenges: brand protection at continental scale, crisis response measured in hours not weeks, competitive positioning that demands intelligence no conventional firm provides, and digital transformation that cannot be entrusted to consultants who have never operated at sovereign scale."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors", href: "/sectors/" }, { label: "Corporate & Enterprise" }]}
        badge="Sovereign-grade for enterprise"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              Enterprise advisory rooted in sovereign-scale operations.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The enterprise sector draws from three of five interconnected strategic domains — Perception, Privacy, and Policy — to address the distinct challenges that arise when organizations operate across jurisdictions, compete at scale, and face threats that conventional approaches cannot neutralize. Dr. Jyoti Kush&apos;s role as Chief Operating Officer places her at the intersection of strategy and execution. She does not design frameworks that sit on shelves. She oversees the deployment of integrated architecture in coordinated configurations that produce verified outcomes.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The platform ecosystem operates through the neural command interface that unifies all capabilities into a single operational fabric. When an enterprise crisis activates Crisis Response Engine, the system simultaneously coordinates intelligence gathering, narrative deployment, and communication security. The compound effect is response speed and completeness no single-platform solution can achieve.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Organizations that engage Dr. Jyoti Kush are not hiring a consultant. They are engaging an operator whose entire career has been building and running systems where failure is not an option. The diagnostic method — evidence before prescription, protocol under pressure, accountability for outcomes — is not a metaphor applied to business. It is an actual operating system, refined through fifteen-plus years of sovereign-scale deployments, and deployed with the same discipline in enterprise engagements as in presidential campaigns and national security operations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <AlertTriangle className="size-3.5 text-red" />
            <span className="text-overline text-red">The Corporate Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">Six challenges Fortune 500 enterprises face daily.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The challenges facing Fortune 100 enterprises and multinational corporations are not theoretical. They are operational, immediate, and consequential. A single reputational crisis can erase billions in market capitalization within hours. A competitive intelligence gap can surrender market share that took years to build.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHALLENGES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <c.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Briefcase className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Enterprise Platform Stack</span>
          </div>
          <h2 className="text-display-md mb-4">Four platforms. One integrated ecosystem.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each platform is built in-house, refined through mission-critical deployment across fifteen-plus years, running on data centers, mainframes, and supercomputers that took a decade to construct. No external platform, no off-the-shelf solution, no licensed technology contributes to these capabilities.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {PLATFORMS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <ShieldCheck className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md ${a.text}`}>{p.title}</h3>
                    <span className="text-overline text-muted-foreground">{p.subtitle}</span>
                    <p className="text-body-sm text-muted-foreground leading-relaxed mt-3">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Three-Pillar Configuration</span>
          </div>
          <h2 className="text-display-md mb-4">How the framework serves enterprise clients.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Enterprise engagements draw from three pillars of the Penta P Framework: Perception (reputation, crisis, brand visibility), Privacy (encrypted communications, zero-trust security, data sovereignty), and Policy (governance transformation, compliance automation, digital infrastructure).
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Enterprise Services</span>
          </div>
          <h2 className="text-display-md mb-4">Six service domains for corporate and enterprise clients.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each service domain addresses a distinct enterprise challenge with the integrated platform architecture, coordinated by the neural command interface that unifies all capabilities into a single operational fabric.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Users className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from this engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Fortune 500 enterprises and multinational corporations that measure by outcomes — not by deliverables, advisory hours, or generic reach metrics. The same sovereign-grade infrastructure deployed for governments, not a diluted version.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{c.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <span className="text-overline text-denim">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Corporate Engagement Timeline.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six milestones from initial intelligence gathering through perpetual operations. The methodology compounds with each engagement — the system that enters the next deployment is smarter than the one that entered the last.
          </p>
        </div>
        <TimelineBlock
          milestones={[
            { period: "Weeks 1-3", title: "Intelligence Baseline", body: "Comprehensive audit of the enterprise's intelligence landscape, security posture, brand perception, and competitive environment. CLAIRVOYANCE CX maps digital surface. S3-SENTINEL audits security infrastructure.", icon: <Eye className="size-4" />, accent: "denim" },
            { period: "Weeks 4-6", title: "Architecture Deployment", body: "Integrated platform configuration tailored to enterprise requirements. Workflow automations, system connectors, and pre-built service templates configured for the operational environment.", icon: <Briefcase className="size-4" />, accent: "red" },
            { period: "Weeks 7-9", title: "Capability Activation", body: "Crisis Response Engine positions response playbooks. Intelligence Operations Layer activates monitoring. Narrative Engineering Layer deploys visibility architecture. S3-SENTINEL establishes security baseline.", icon: <Activity className="size-4" />, accent: "cyan" },
            { period: "Weeks 10-12", title: "Steady-State Operations", body: "Continuous 24/7 monitoring with daily briefs, weekly assessments, and monthly strategic reviews. Real-time alerting calibrated to severity and recipient role across the executive team.", icon: <TrendingUp className="size-4" />, accent: "purple" },
            { period: "Quarter 2+", title: "Performance Optimization", body: "Quarterly intelligence effectiveness review. Adaptive model retraining. Source addition or removal. Product format evolution based on operational outcomes and stakeholder feedback.", icon: <Target className="size-4" />, accent: "emerald" },
            { period: "Ongoing", title: "Perpetual Partnership", body: "The engagement does not end. The feedback loop continuously strengthens the enterprise's posture. Each threat detected, each crisis averted, each campaign refined — cumulative advantage that compounds.", icon: <Award className="size-4" />, accent: "amber-gold" },
          ]}
          orientation="horizontal"
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <span className="text-overline text-purple">Enterprise Performance Data</span>
          </div>
          <h2 className="text-display-md mb-4">Verified outcomes from enterprise engagements.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The metrics that follow are drawn from operational data across enterprise deployments. The 347% average ROI traces to financial results — recovered public funds, avoided crisis costs, and revenue protected through reputation management.
          </p>
        </div>
        <CounterBlock
          columns={3}
          stats={[
            { value: 347, suffix: "%", label: "Average ROI", description: "Three-year deployment across enterprise clients", accent: "amber-gold" },
            { value: 384, suffix: "x-1,416x", label: "Crisis Speed", description: "Versus traditional crisis management approaches", accent: "red" },
            { value: 85, suffix: "-95%", label: "Crisis Success Rate", description: "Crisis Response Engine deployment outcomes", accent: "cyan" },
            { value: 99.9999, suffix: "%", label: "Uptime", description: "Zero security incidents across 15+ years", accent: "purple" },
            { value: 89, suffix: "%", label: "Prediction Accuracy", description: "Crisis forecasting across 195+ countries", accent: "emerald" },
            { value: 3.7, suffix: "mo", label: "Payback Period", description: "S3-SENTINEL deployment cost recovery", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <span className="text-overline text-cyan">Enterprise Service Portfolio</span>
          </div>
          <h2 className="text-display-md mb-4">Six service domains for corporate and enterprise clients.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each service domain addresses a distinct enterprise challenge with the integrated platform architecture, coordinated by the neural command interface that unifies all capabilities into a single operational fabric.
          </p>
        </div>
        <FeatureGridBlock
          columns={3}
          features={[
            { title: "Brand Protection", body: "300-500% improvement in narrative penetration across 50+ digital platforms with 95-98% organic authenticity.", icon: <Eye className="size-5" />, accent: "red" },
            { title: "Crisis Response", body: "Sub-5-minute total response, 384x-1,416x speed advantage, 85-95% success rate across twelve crisis categories.", icon: <Flame className="size-5" />, accent: "amber-gold" },
            { title: "Competitive Intelligence", body: "75% competitive move anticipation, 80% regulatory prediction accuracy across 195+ countries.", icon: <TrendingUp className="size-5" />, accent: "cyan" },
            { title: "Secure Communications", body: "Quantum-resistant encryption, FIPS 140-3 Level 3 HSMs, air-gapped deployment for M&A and board communications.", icon: <Lock className="size-5" />, accent: "purple" },
            { title: "Digital Transformation", body: "72% staff productivity improvement, 65% inter-departmental coordination improvement, 410% digital service adoption.", icon: <Activity className="size-5" />, accent: "emerald" },
            { title: "Regulatory Navigation", body: "Real-time monitoring across 195+ countries. 80% predictive accuracy. GDPR, HIPAA, PCI DSS, and 50+ pre-built frameworks.", icon: <Scale className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Enterprise engagements begin with the same discipline that has produced zero security incidents and three hundred forty-seven percent average ROI across deployments. National security level confidentiality from first contact. The standard is sovereign. The approach is the same.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/sectors/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Compass className="size-4" />
                All Sectors
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Governments", description: "Governance transformation at sovereign scale.", href: "/sectors/governments/", icon: Building2, accent: "red" },
          { title: "Defense & Intelligence", description: "Sovereign-grade security and intelligence.", href: "/sectors/defense-intelligence/", icon: ShieldCheck, accent: "emerald" },
          { title: "Public Figures & HNWIs", description: "Reputation and privacy for prominent individuals.", href: "/sectors/public-figures-hnwis/", icon: Users, accent: "coral" },
          { title: "Sectors Hub", description: "Eight sectors. One operating system.", href: "/sectors/", icon: Briefcase, accent: "denim" },
        ]}
      />
    </>
  );
}
